/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import type { StyleFunction, StyleLike } from 'ol/style/Style';
import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { layerTypes, CoreAppStore } from '@opengeoweb/store';
import {
  layerActions,
  drawingToolSelectors,
  layerUtils,
} from '@opengeoweb/store';
import type {
  BoxSelect,
  DrawModeExitCallback,
  DrawModeValue,
  HoverSelect,
  ModifyFeaturesGeoJSON,
  MoveFeaturesGeoJSON,
  MovedFeature,
} from '@opengeoweb/webmap-react';
import {
  DRAWMODE,
  OpenLayersFeatureLayer,
  OpenLayersMapDraw,
  calculateBoxSelection,
  getOlFeatureKey,
  calculateNewSelection,
  getAffectedIndices,
  isGeoJSONGeometryEmpty,
  shouldEnterEditMode,
  shouldExitEditModeForMultiSelect,
} from '@opengeoweb/webmap-react';

export { getAffectedIndices };

// OpenLayers counterpart of layerUtils.isHiddenFeature
const isHiddenOlFeature = (feature: FeatureLike): boolean =>
  feature.get('isHidden') === true;

export const hideHiddenFeatures =
  (style: StyleLike): StyleFunction =>
  (feature, resolution) => {
    if (isHiddenOlFeature(feature)) {
      return undefined;
    }
    return typeof style === 'function' ? style(feature, resolution) : style;
  };

/** Escape drops a multi selection only. A sole selection is kept, matching the
 * plain click behaviour, unless the drawing tool is open. Edit mode keeps the
 * map's own Escape handling, after which onDrawEscape drops the selection. */
export const shouldDeselectAllOnEscape = (
  selectedFeatureCount: number,
  isInEditMode: boolean | undefined,
  hasRegisteredDrawTool: boolean,
  isDrawToolOpen = false,
): boolean =>
  hasRegisteredDrawTool &&
  !isInEditMode &&
  selectedFeatureCount > (isDrawToolOpen ? 0 : 1);

interface DeselectAllOnEscapeOptions {
  selectedFeatureCount: number;
  isInEditMode: boolean | undefined;
  hasRegisteredDrawTool: boolean;
  isDrawToolOpen?: boolean;
  onDeselectAll: () => void;
}

export const useDeselectAllOnEscape = ({
  selectedFeatureCount,
  isInEditMode,
  hasRegisteredDrawTool,
  isDrawToolOpen,
  onDeselectAll,
}: DeselectAllOnEscapeOptions): void => {
  React.useEffect(() => {
    if (
      !shouldDeselectAllOnEscape(
        selectedFeatureCount,
        isInEditMode,
        hasRegisteredDrawTool,
        isDrawToolOpen,
      )
    ) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || document.activeElement !== document.body) {
        return;
      }
      onDeselectAll();
    };

    window.addEventListener('keydown', onKeyDown);
    return (): void => window.removeEventListener('keydown', onKeyDown);
  }, [
    selectedFeatureCount,
    isInEditMode,
    hasRegisteredDrawTool,
    isDrawToolOpen,
    onDeselectAll,
  ]);
};

export const shouldClearSelectionOnEmptyClick = (
  activeDrawToolId: string | undefined,
  hasRegisteredDrawTool: boolean,
): boolean => !activeDrawToolId && hasRegisteredDrawTool;

export const isSketchInProgress = (
  isInEditMode: boolean | undefined,
  drawMode: string | undefined,
  activeFeature: GeoJSON.Feature | undefined,
): boolean => {
  if (!isInEditMode) {
    return false;
  }
  if (drawMode === DRAWMODE.SPLIT) {
    return true;
  }
  return Boolean(activeFeature && isGeoJSONGeometryEmpty(activeFeature));
};

/** Soundings and time series set their own highlight, so they must stay out of this. */
export const usesFeatureIdSelection = (
  isSelectable: boolean | undefined,
  hasRegisteredDrawTool: boolean,
): boolean => Boolean(isSelectable) || hasRegisteredDrawTool;

export const getSelectionProperties = (
  usesIdSelection: boolean,
  selectedFeatureIdSet: Set<string>,
  featureId: string | undefined,
): { isSelected: boolean; highlightSelectedFeature: boolean } | undefined => {
  if (!usesIdSelection) {
    return undefined;
  }
  const isSelected =
    featureId !== undefined && selectedFeatureIdSet.has(featureId);
  return { isSelected, highlightSelectedFeature: isSelected };
};

export const getClickedFeatureKey = getOlFeatureKey;

export const findFeatureIndexByKey = (
  geojson: GeoJSON.FeatureCollection | undefined,
  feature?: Feature,
): number => {
  const key = getClickedFeatureKey(feature);
  // -1 when the feature has no key, so an unidentifiable click never matches feature 0
  if (key === undefined || !geojson) {
    return -1;
  }
  return layerUtils.findFeatureIndexById(geojson, key);
};

export const getMovableSelectedFeatureIds = (
  geojson: GeoJSON.FeatureCollection | undefined,
  selectedFeatureIds: string[] | undefined,
): string[] => {
  if (!geojson?.features.length || !selectedFeatureIds?.length) {
    return [];
  }

  const selectedFeatureIdSet = new Set(selectedFeatureIds);
  return geojson.features.reduce<string[]>((featureIds, feature) => {
    const featureKey = layerUtils.getFeatureSelectionKey(feature);
    if (
      featureKey &&
      selectedFeatureIdSet.has(featureKey) &&
      feature.properties?.isReadonly !== true
    ) {
      featureIds.push(featureKey);
    }
    return featureIds;
  }, []);
};

/** Moving a set of shapes belongs to the drawing tool only: selection-only
 * reference layers stay selectable but must never be dragged. */
export const shouldEnableMultiShapeMove = (
  hasRegisteredDrawTool: boolean,
  isInEditMode: boolean | undefined,
  selectedFeatureCount: number,
  movableFeatureCount: number,
): boolean =>
  hasRegisteredDrawTool &&
  !isInEditMode &&
  selectedFeatureCount > 1 &&
  movableFeatureCount > 0;

interface SelectionState {
  geojson?: GeoJSON.FeatureCollection;
  selectedFeatureIds?: string[];
}

export const getCurrentSelectedIndices = ({
  geojson,
  selectedFeatureIds,
}: SelectionState): number[] => {
  if (!geojson?.features.length || !selectedFeatureIds?.length) {
    return [];
  }

  const selectedFeatureIdSet = new Set(selectedFeatureIds);
  return geojson.features.reduce<number[]>((indices, feature, index) => {
    const featureKey = layerUtils.getFeatureSelectionKey(feature);
    if (featureKey && selectedFeatureIdSet.has(featureKey)) {
      indices.push(index);
    }
    return indices;
  }, []);
};

interface OpenLayersFeatureLayerConnectProps {
  layer: layerTypes.ReduxLayer;
  layerId?: string;
  zIndex: number;
  style: StyleLike;
  hoverSelect?: HoverSelect;
}

export const OpenLayersFeatureLayerConnect: React.FC<
  OpenLayersFeatureLayerConnectProps
> = ({
  layer,
  layerId,
  zIndex,
  style,
  hoverSelect,
}: OpenLayersFeatureLayerConnectProps) => {
  const store = useStore<CoreAppStore>();
  const dispatch = useDispatch();
  const activeDrawToolId = useSelector(
    drawingToolSelectors.getActiveDrawToolId,
  );
  const hasRegisteredDrawTool = useSelector((state: CoreAppStore) =>
    drawingToolSelectors.getHasDrawToolForLayer(state, layerId),
  );
  const isDrawToolOpen = useSelector((state: CoreAppStore) =>
    drawingToolSelectors.getIsDrawToolOpenForLayer(state, layerId),
  );
  const layerStyle = React.useMemo(
    () => (isDrawToolOpen ? hideHiddenFeatures(style) : style),
    [isDrawToolOpen, style],
  );
  const activeDrawMode = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getActiveDrawMode(store, activeDrawToolId),
  );
  const shouldAllowMultipleShapes = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getShouldAllowMultipleShapes(store, activeDrawToolId),
  );
  const geoJSONIntersectionLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionLayerId(store, activeDrawToolId),
  );
  const geoJSONIntersectionBoundsLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionBoundsLayerId(
      store,
      activeDrawToolId,
    ),
  );
  const activeFeatureIndex = React.useMemo(() => {
    const currentActiveFeatureIndex = layerUtils.getSelectedFeatureIndexByIds(
      layer.geojson,
      layer.selectedFeatureIds,
    );

    if (
      !shouldAllowMultipleShapes ||
      !activeDrawMode?.selectionType ||
      !layer.geojson?.features.length
    ) {
      return currentActiveFeatureIndex ?? 0;
    }

    const currentSelectedFeature =
      currentActiveFeatureIndex !== undefined
        ? layer.geojson.features[currentActiveFeatureIndex]
        : undefined;

    if (
      currentSelectedFeature?.properties?.selectionType ===
      activeDrawMode.selectionType
    ) {
      return currentActiveFeatureIndex ?? 0;
    }

    let matchingFeatureIndex: number | undefined;

    for (
      let index = layer.geojson.features.length - 1;
      index >= 0;
      index -= 1
    ) {
      const feature = layer.geojson.features[index];

      if (feature.properties?.selectionType === activeDrawMode.selectionType) {
        if (isGeoJSONGeometryEmpty(feature)) {
          return index;
        }

        if (matchingFeatureIndex === undefined) {
          matchingFeatureIndex = index;
        }
      }
    }

    return matchingFeatureIndex ?? currentActiveFeatureIndex ?? 0;
  }, [
    activeDrawMode?.selectionType,
    layer.geojson,
    layer.selectedFeatureIds,
    shouldAllowMultipleShapes,
  ]);

  const sketchInProgress = React.useMemo(
    () =>
      isSketchInProgress(
        layer.isInEditMode,
        layer.drawMode,
        layer.geojson?.features[activeFeatureIndex],
      ),
    [layer.isInEditMode, layer.drawMode, layer.geojson, activeFeatureIndex],
  );

  const usesIdSelection = usesFeatureIdSelection(
    layer.isSelectable,
    hasRegisteredDrawTool,
  );

  const displayGeojson = React.useMemo(() => {
    if (!layer.geojson) {
      return undefined;
    }

    const hoveredFeatureIds = new Set(layer.hoveredFeatureIds ?? []);
    const selectedFeatureIdSet = new Set(layer.selectedFeatureIds ?? []);

    return {
      ...layer.geojson,
      features: layer.geojson.features.map((feature) => {
        const featureId =
          feature.id !== undefined ? String(feature.id) : undefined;
        const originalId =
          typeof feature.properties?.originalId === 'string'
            ? feature.properties.originalId
            : layer.originalId;

        const selectionProperties = getSelectionProperties(
          usesIdSelection,
          selectedFeatureIdSet,
          featureId,
        );

        return {
          ...feature,
          properties: {
            ...feature.properties,
            hover:
              (featureId !== undefined && hoveredFeatureIds.has(featureId)) ||
              (typeof originalId === 'string' &&
                hoveredFeatureIds.has(originalId)),
            ...selectionProperties,
          },
        };
      }),
    };
  }, [
    layer.geojson,
    layer.hoveredFeatureIds,
    layer.originalId,
    layer.selectedFeatureIds,
    usesIdSelection,
  ]);

  const clearSelection = React.useCallback((): void => {
    if (
      !shouldClearSelectionOnEmptyClick(
        drawingToolSelectors.getActiveDrawToolId(store.getState()),
        hasRegisteredDrawTool,
      )
    ) {
      return;
    }
    dispatch(
      layerActions.setSelectedFeatures({
        layerId: layerId!,
        selectedFeatureIds: [],
      }),
    );
    dispatch(
      layerActions.toggleFeatureMode({
        layerId: layerId!,
        isInEditMode: false,
        drawMode: '',
      }),
    );
  }, [store, hasRegisteredDrawTool, dispatch, layerId]);

  const onDrawEscape = React.useCallback((): void => {
    // the map already ended its own edit or draw mode for this Escape
    if (
      document.activeElement === document.body &&
      drawingToolSelectors.getIsDrawToolOpenForLayer(store.getState(), layerId)
    ) {
      clearSelection();
    }
  }, [store, layerId, clearSelection]);

  const onFeatureClick = React.useCallback(
    (feature: Feature[], isShiftClick?: boolean) => {
      if (!feature.length) {
        clearSelection();
        return;
      }

      const clickedIndex = findFeatureIndexByKey(layer.geojson, feature[0]);

      if (clickedIndex === -1) {
        return;
      }

      const clickedFeature = layer.geojson!.features[clickedIndex];
      const isReadonly = clickedFeature?.properties?.isReadonly === true;

      const currentSelection = getCurrentSelectedIndices({
        geojson: layer.geojson,
        selectedFeatureIds: layer.selectedFeatureIds,
      });

      // Drawing layers only: deselecting the active edit target collapses edit mode,
      // so a plain click on the sole selection keeps it. Clearing happens through
      // empty space or shift+click instead.
      const shouldPreventDeselect =
        hasRegisteredDrawTool &&
        !isReadonly &&
        !isShiftClick &&
        currentSelection.length === 1 &&
        currentSelection[0] === clickedIndex;

      const newSelection = shouldPreventDeselect
        ? [clickedIndex]
        : calculateNewSelection(
            currentSelection,
            clickedIndex,
            isShiftClick ?? false,
          );

      const nextSelectedFeatureIds = newSelection
        .map((index) =>
          layerUtils.getFeatureSelectionKey(layer.geojson!.features[index]),
        )
        .filter((featureId): featureId is string => Boolean(featureId));

      dispatch(
        layerActions.setSelectedFeatures({
          layerId: layerId!,
          selectedFeatureIds: nextSelectedFeatureIds,
        }),
      );

      if (
        newSelection.length === 0 ||
        shouldExitEditModeForMultiSelect(newSelection.length)
      ) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: false,
            drawMode: '',
          }),
        );
      }

      if (
        shouldEnterEditMode(
          isReadonly,
          isShiftClick ?? false,
          newSelection.length,
        )
      ) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: true,
            drawMode: '',
          }),
        );
      }
    },
    [
      hasRegisteredDrawTool,
      dispatch,
      layer.geojson,
      layer.selectedFeatureIds,
      layerId,
      clearSelection,
    ],
  );

  const onNoFeatureClick = clearSelection;

  const onBoxSelect = React.useCallback(
    (features: Feature[], addToSelection: boolean): void => {
      if (!layer.geojson?.features.length) {
        return;
      }

      const boxSelectedIndices = features
        .filter((feature) => !(isDrawToolOpen && isHiddenOlFeature(feature)))
        .map((feature) => findFeatureIndexByKey(layer.geojson, feature))
        .filter((idx) => idx !== -1);

      if (boxSelectedIndices.length === 0) {
        return;
      }

      const previousSelection = getCurrentSelectedIndices({
        geojson: layer.geojson,
        selectedFeatureIds: layer.selectedFeatureIds,
      });

      const newSelection = calculateBoxSelection(
        previousSelection,
        boxSelectedIndices,
        addToSelection,
      );

      const nextSelectedFeatureIds = newSelection
        .map((index) =>
          layerUtils.getFeatureSelectionKey(layer.geojson!.features[index]),
        )
        .filter((featureId): featureId is string => Boolean(featureId));

      dispatch(
        layerActions.setSelectedFeatures({
          layerId: layerId!,
          selectedFeatureIds: nextSelectedFeatureIds,
        }),
      );

      if (shouldExitEditModeForMultiSelect(newSelection.length)) {
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: false,
            drawMode: '',
          }),
        );
      }
    },
    [
      dispatch,
      layer.geojson,
      layer.selectedFeatureIds,
      layerId,
      isDrawToolOpen,
    ],
  );

  const boxSelectConfig = React.useMemo<BoxSelect | undefined>(
    () =>
      usesIdSelection && !layer.isInEditMode
        ? { onBoxSelect, active: true }
        : undefined,
    [usesIdSelection, layer.isInEditMode, onBoxSelect],
  );

  // Mutually exclusive with boxSelectConfig, so only one DragBox is ever live.
  const editModeBoxSelectConfig = React.useMemo<BoxSelect | undefined>(
    () =>
      usesIdSelection && layer.isInEditMode
        ? { onBoxSelect, active: true }
        : undefined,
    [usesIdSelection, layer.isInEditMode, onBoxSelect],
  );

  const selectedFeatureIndices = React.useMemo(
    () =>
      getCurrentSelectedIndices({
        geojson: layer.geojson,
        selectedFeatureIds: layer.selectedFeatureIds,
      }),
    [layer.geojson, layer.selectedFeatureIds],
  );

  useDeselectAllOnEscape({
    selectedFeatureCount: selectedFeatureIndices.length,
    isInEditMode: layer.isInEditMode,
    hasRegisteredDrawTool,
    isDrawToolOpen,
    onDeselectAll: clearSelection,
  });

  const movableSelectedFeatureIds = React.useMemo(
    () => getMovableSelectedFeatureIds(layer.geojson, layer.selectedFeatureIds),
    [layer.geojson, layer.selectedFeatureIds],
  );

  const onFeatureGeometriesChange = React.useCallback(
    (changedFeatures: MovedFeature[]): void => {
      if (!layerId || !changedFeatures.length) {
        return;
      }
      dispatch(
        layerActions.moveFeatures({
          layerId,
          movedFeatures: changedFeatures,
          geoJSONIntersectionLayerId,
          geoJSONIntersectionBoundsLayerId,
        }),
      );
    },
    [
      dispatch,
      layerId,
      geoJSONIntersectionLayerId,
      geoJSONIntersectionBoundsLayerId,
    ],
  );

  const isMultiShapeInteractionEnabled = shouldEnableMultiShapeMove(
    hasRegisteredDrawTool,
    layer.isInEditMode,
    layer.selectedFeatureIds?.length ?? 0,
    movableSelectedFeatureIds.length,
  );

  const moveFeaturesConfig = React.useMemo<MoveFeaturesGeoJSON | undefined>(
    () =>
      isMultiShapeInteractionEnabled
        ? { onMoveEnd: onFeatureGeometriesChange, active: true }
        : undefined,
    [isMultiShapeInteractionEnabled, onFeatureGeometriesChange],
  );

  const modifyFeaturesConfig = React.useMemo<ModifyFeaturesGeoJSON | undefined>(
    () =>
      isMultiShapeInteractionEnabled
        ? { onModifyEnd: onFeatureGeometriesChange, active: true }
        : undefined,
    [isMultiShapeInteractionEnabled, onFeatureGeometriesChange],
  );

  const onViewOnlyFeatureClick = React.useCallback(
    (feature: Feature[], isShiftClick?: boolean): void => {
      if (sketchInProgress) {
        return;
      }
      onFeatureClick(feature, isShiftClick);
    },
    [sketchInProgress, onFeatureClick],
  );

  const updateFeature = React.useCallback(
    (geojson: GeoJSON.FeatureCollection, reason: string): void => {
      if (!layerId) {
        window.console.error('updateFeature without layerId');
        return;
      }

      dispatch(
        layerActions.updateFeature({
          geojson,
          reason,
          layerId,
          shouldAllowMultipleShapes,
          geoJSONIntersectionLayerId,
          geoJSONIntersectionBoundsLayerId,
          selectionType: activeDrawMode?.selectionType,
        }),
      );
    },
    [
      layerId,
      dispatch,
      shouldAllowMultipleShapes,
      geoJSONIntersectionLayerId,
      geoJSONIntersectionBoundsLayerId,
      activeDrawMode?.selectionType,
    ],
  );

  const exitFeatureDrawMode = React.useCallback(
    (reason: DrawModeExitCallback): void => {
      if (!layerId) {
        window.console.error('exitFeatureDrawMode without layerId');
        return;
      }
      const activeDrawToolId = drawingToolSelectors.getActiveDrawToolId(
        store.getState(),
      );

      dispatch(
        layerActions.exitFeatureDrawMode({
          reason,
          layerId,
          shouldAllowMultipleShapes:
            drawingToolSelectors.getShouldAllowMultipleShapes(
              store.getState(),
              activeDrawToolId,
            ),
        }),
      );
    },
    [dispatch, store, layerId],
  );

  return (
    <>
      <OpenLayersFeatureLayer
        boxSelect={boxSelectConfig}
        clickOnFeature={
          usesIdSelection && !layer.isInEditMode
            ? {
                onClick: onFeatureClick,
                hitTolerance: 5,
              }
            : undefined
        }
        featureCollection={!layer.isInEditMode ? displayGeojson : undefined}
        hoverSelect={hoverSelect}
        layerId={layerId}
        modifyFeatures={modifyFeaturesConfig}
        moveFeatures={moveFeaturesConfig}
        opacity={layer.opacity}
        style={layerStyle}
        visible={layer.enabled}
        zIndex={zIndex}
      />
      <OpenLayersMapDraw
        boxSelect={editModeBoxSelectConfig}
        drawMode={layer.drawMode as DrawModeValue}
        exitDrawModeCallback={exitFeatureDrawMode}
        geojson={layer.geojson!}
        isInEditMode={layer.isInEditMode || false}
        onEscape={onDrawEscape}
        onNoFeatureClick={onNoFeatureClick}
        onViewOnlyFeatureClick={onViewOnlyFeatureClick}
        selectedFeatureIndex={activeFeatureIndex}
        selectedFeatureIndices={selectedFeatureIndices}
        updateGeojson={updateFeature}
        viewOnlyStyle={layerStyle}
      />
    </>
  );
};
